import { isEqual } from "lodash";
import * as React from "react";

import { Msg } from "@shared/components";
import {
  ApplicantExamModel,
  ApplicantChallengeModel,
  ApplicantExamTimerModel,
  ApplicantChallengeTimerModel,
} from "@shared/models";
import { secondsOf5Minutes } from "@shared/services/date";
import { ChallengeStyle } from "@shared/services/enums";
import History from "@shared/services/history";
import Logger from "@shared/services/logger";

import FinalChallengeSubmit from "../../../modals/finalChallengeSubmit/FinalChallengeSubmit";
import { AutoSubmittedChallenge } from "../autoSubmitChallenge/AutoSubmitChallenge";

export interface TimerHeadquarterProps {
  tutorial: boolean;
  exam: ApplicantExamModel;
  timer: {
    exam: ApplicantExamTimerModel;
    challenges: ApplicantChallengeTimerModel[];
    error?: boolean;
  };
  getExamTimeLeft: () => void;
  getChallengeTimeLeft: (challengeId: number, resultId: number) => void;
  submitExam: () => void;
  addNotification: (item: React.ReactElement) => void;
  tick: () => void;
}

interface TimerHeadquarterState {
  isOpenFinalChallengeSubmit: boolean;
  autoSubmittedChallenge?: ApplicantChallengeModel;
}

const ONE_SECOND = 1000;
const ONE_MINUTE = 1000 * 60;

export class TimerHeadquarter extends React.Component<
  TimerHeadquarterProps,
  TimerHeadquarterState
> {
  private tickTimerId: number;
  private fetchTimerId: number;

  constructor(props: TimerHeadquarterProps) {
    super(props);
    this.state = {
      isOpenFinalChallengeSubmit: false,
    };
  }

  public componentDidMount() {
    this.onFetch();
    this.tickTimerId = window.setInterval(this.props.tick, ONE_SECOND);
    this.fetchTimerId = window.setInterval(this.onFetch, ONE_MINUTE);
  }

  public shouldComponentUpdate(
    nextProps: TimerHeadquarterProps,
    nextState: TimerHeadquarterState,
  ) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  public componentDidUpdate(prevProps: Readonly<TimerHeadquarterProps>) {
    const { exam, timer, tutorial } = this.props;
    // handle deadline has extended
    if (timer.error) {
      History.push(
        `/${tutorial ? "tutorial" : exam.org.name}/exams/${
          exam.urlToken
        }/challenges`,
      );
      window.location.reload();
    }
    // handle start exam
    if (!prevProps.exam.isStarted() && exam.isStarted()) {
      this.props.getExamTimeLeft();
    }
    // handle exam submitted/expired
    if (!prevProps.exam.isFinished() && exam.isFinished()) {
      this.clearInterval();
    }
    // handle start challenge
    if (!isEqual(prevProps.exam.results, exam.results)) {
      this.props.exam.challenges
        .filter((challenge) => exam.isChallengeStarted(challenge.id))
        .filter(
          (challenge) =>
            timer.challenges.find((item) => item.challengeId === challenge.id)
              ?.seconds === undefined,
        )
        .forEach((challenge) => {
          const challengeResult = exam.getResultByChallengeId(challenge.id);
          this.props.getChallengeTimeLeft(challenge.id, challengeResult.id);
        });
    }
    // handle tick
    if (!isEqual(prevProps.timer, this.props.timer)) {
      this.checkTimeUp();
    }
  }

  public componentWillUnmount() {
    this.clearInterval();
  }

  public render() {
    const { exam, timer, addNotification } = this.props;
    const { isOpenFinalChallengeSubmit } = this.state;
    const isExamTimeout = (timer.exam.seconds ?? 0) <= 0;

    return (
      <div>
        {timer.challenges.map((challenge) => (
          <AutoSubmittedChallenge
            key={challenge.challengeId}
            challenge={challenge}
            exam={exam}
            isExamTimeout={isExamTimeout}
            addNotification={addNotification}
            onChallengeTimeUp={this.onChallengeTimeUp}
          />
        ))}
        <FinalChallengeSubmit
          isOpen={isOpenFinalChallengeSubmit}
          onClose={this.onCloseFinalChallengeSubmit}
          challenge={this.state.autoSubmittedChallenge}
        />
      </div>
    );
  }

  private onFetch = () => {
    const { exam } = this.props;
    this.props.getExamTimeLeft();
    exam.challenges
      .filter((challenge) => exam.isChallengeStarted(challenge.id))
      .forEach((challenge) => {
        const challengeResult = exam.getResultByChallengeId(challenge.id);
        Logger.info(
          `challenge left timer run: challengeId=${challenge.id}, challengeResultId=${challengeResult.id}`,
        );
        this.props.getChallengeTimeLeft(challenge.id, challengeResult.id);
      });
  };

  private onExamTimeUp = () => {
    const { exam } = this.props;
    this.clearInterval();
    if (exam.canSubmit()) {
      this.props.submitExam();
    }
  };

  private onExam5MinutesLeft = () => {
    const { exam } = this.props;
    this.props.addNotification(
      <Msg
        id={"message.applicant.examTimeupBefore5mins"}
        values={{
          exam: exam.exam.name,
        }}
      />,
    );
  };

  private onChallengeTimeUp = (challengeId: number) => {
    const { exam } = this.props;

    const challenge = exam.getChallengeByChallengeId(challengeId);

    if (!this.state.isOpenFinalChallengeSubmit) {
      this.setState({
        isOpenFinalChallengeSubmit: true,
        autoSubmittedChallenge: challenge,
      });
    }
  };

  private onCloseFinalChallengeSubmit = () => {
    const { exam } = this.props;
    const { autoSubmittedChallenge } = this.state;

    if (
      autoSubmittedChallenge &&
      ChallengeStyle.hasAppealComment(autoSubmittedChallenge.style)
    ) {
      History.push(
        `/${exam.org.name}/exams/${exam.urlToken}/appeal/${autoSubmittedChallenge.id}`,
      );
    } else {
      History.push(`/${exam.org.name}/exams/${exam.urlToken}/challenges`);
    }

    this.setState({
      isOpenFinalChallengeSubmit: false,
      autoSubmittedChallenge: undefined,
    });
  };

  private checkTimeUp = () => {
    const { exam } = this.props.timer;

    if (exam.seconds === undefined) {
      return;
    }

    // handle time up and 5 mins left
    if (exam.seconds === secondsOf5Minutes) {
      this.onExam5MinutesLeft();
    }
    if (exam.seconds === 0) {
      this.onExamTimeUp();
    }
  };

  private clearInterval = () => {
    clearInterval(this.tickTimerId);
    clearInterval(this.fetchTimerId);
  };
}
